import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, FileDown, Plus, Pencil, Check, Trash2, ChevronDown, ChevronLeft, ChevronRight, Camera, MessageCircle, X, CalendarClock, CalendarPlus, Users, LogOut, Play } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import { hasPermission } from '../../utils/permissions';
import { strBasePath, strPropertyQuery, useStrProperty } from './properties';
import { AuthedImage, CleaningJobDetail, CleaningJobsPanel, JOB_STATUS } from './CleaningJobs';
import { exportChecklistPdf } from './checklistPdf';
import { NO_NEXT_STAY_TEXT, resolveTaskTitle } from './taskFormula';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const formatDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toYmd = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const describeSchedule = (checklist) => {
  const rules = [];
  if (checklist.booking_every) {
    rules.push(checklist.booking_every === 1 ? 'after every booking' : `every ${checklist.booking_every} bookings`);
  }
  if (checklist.interval_every) {
    const unit = checklist.interval_unit === 'months' ? 'month' : 'week';
    rules.push(checklist.interval_every === 1 ? `every ${unit}` : `every ${checklist.interval_every} ${unit}s`);
  }
  let text = rules.join(' or ');
  if (rules.length > 1) text += ', whichever comes first';
  if (checklist.season_start_month && checklist.season_end_month) {
    text += ` · only ${MONTHS[checklist.season_start_month - 1]}–${MONTHS[checklist.season_end_month - 1]}`;
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Returns { label, color, background } for the checklist's due status.
const describeDue = (checklist) => {
  const bookingProgress = checklist.booking_every > 1
    ? ` · ${checklist.bookings_since}/${checklist.booking_every} bookings`
    : '';
  const prefix = checklist.due_source === 'extra' ? 'Extra: ' : '';

  if (!checklist.due_date) {
    return { label: `Waiting for bookings${bookingProgress}`, color: '#64748b', background: '#f1f5f9' };
  }
  const days = checklist.days_until_due;
  if (days < 0) {
    return { label: `${prefix}Overdue by ${plural(-days, 'day')} (${formatDate(checklist.due_date)})${bookingProgress}`, color: '#b91c1c', background: '#fef2f2' };
  }
  if (days === 0) {
    return { label: `${prefix}Due today${bookingProgress}`, color: '#b45309', background: '#fffbeb' };
  }
  return { label: `${prefix}Due in ${plural(days, 'day')} (${formatDate(checklist.due_date)})${bookingProgress}`, color: '#047857', background: '#ecfdf5' };
};

const describeAssignees = (checklist) => (
  checklist.assignees.length === 0 ? 'Anyone' : checklist.assignees.map((a) => a.displayName).join(', ')
);

// Asks the server to move an item and returns the new order of its siblings as ids.
const requestMove = async (route, id, direction) => {
  const response = await authFetch(`${API_BASE}/api/cleaning/${route}/${id}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ direction })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to change the order');
  return data.ids;
};

const applyOrder = (items, ids) => [...items].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));

// Up and down arrows for an item in a list. `index` and `count` place it among its siblings.
const MoveButtons = ({ name, index, count, onMove, busy }) => (
  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
    <button onClick={() => onMove('up')} disabled={busy || index === 0} style={moveButtonStyle(busy || index === 0)} title="Move up" aria-label={`Move ${name} up`}>
      <ArrowUp size={14} />
    </button>
    <button onClick={() => onMove('down')} disabled={busy || index === count - 1} style={moveButtonStyle(busy || index === count - 1)} title="Move down" aria-label={`Move ${name} down`}>
      <ArrowDown size={14} />
    </button>
  </div>
);

const CleaningPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEditPermission = hasPermission('canShortTermRentalsCleaningEdit');
  const property = useStrProperty();
  const propertyKey = property.key;

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState(null);
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [movingChecklist, setMovingChecklist] = useState(false);

  const selectedJobId = Number.parseInt(searchParams.get('job'), 10) || null;
  const selectedId = Number.parseInt(searchParams.get('checklist'), 10) || null;
  const selectedChecklist = checklists.find((c) => c.id === selectedId) || null;

  const loadChecklists = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/checklists${strPropertyQuery(propertyKey)}`);
      if (!response.ok) throw new Error('Failed to load checklists');
      const data = await response.json();
      setChecklists(data.checklists || []);
    } catch (err) {
      setError(err.message || 'Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChecklists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyKey]);

  useEffect(() => {
    if (!canEditPermission) return;
    authFetch(`${API_BASE}/api/cleaning/users`)
      .then((response) => (response.ok ? response.json() : { users: [] }))
      .then((data) => setUsers(data.users || []))
      .catch(() => setUsers([]));
  }, [canEditPermission]);

  const replaceChecklist = (checklist) => {
    setChecklists((prev) => prev.map((c) => (c.id === checklist.id ? checklist : c)));
  };

  const openChecklist = (id) => {
    setSearchParams({ checklist: String(id) });
  };

  const backToList = () => {
    setSearchParams({});
    setEditingChecklistId(null);
    loadChecklists();
  };

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setShowNewForm(false);
    setEditingChecklistId(null);
  };

  const handleCreateChecklist = async (values) => {
    setSavingChecklist(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/checklists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, propertyKey })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create checklist');
      setChecklists((prev) => [...prev, data.checklist]);
      setShowNewForm(false);
    } catch (err) {
      setError(err.message || 'Failed to create checklist');
    } finally {
      setSavingChecklist(false);
    }
  };

  const handleUpdateChecklist = async (id, values) => {
    setSavingChecklist(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/checklists/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update checklist');
      replaceChecklist(data.checklist);
      setEditingChecklistId(null);
    } catch (err) {
      setError(err.message || 'Failed to update checklist');
    } finally {
      setSavingChecklist(false);
    }
  };

  const handleDeleteChecklist = async (checklist) => {
    if (!window.confirm(`Delete "${checklist.name}" with all its titles, tasks and history? This cannot be undone.`)) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/checklists/${checklist.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete checklist');
      setChecklists((prev) => prev.filter((c) => c.id !== checklist.id));
      if (selectedId === checklist.id) setSearchParams({});
    } catch (err) {
      setError(err.message || 'Failed to delete checklist');
    }
  };

  const handleMoveChecklist = async (checklist, direction) => {
    setMovingChecklist(true);
    setError('');
    try {
      const ids = await requestMove('checklists', checklist.id, direction);
      setChecklists((prev) => applyOrder(prev, ids));
    } catch (err) {
      setError(err.message || 'Failed to change the order');
    } finally {
      setMovingChecklist(false);
    }
  };

  const openJob = (id) => setSearchParams({ job: String(id) });

  const handleStartNow = async (checklist) => {
    setStartingId(checklist.id);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/checklists/${checklist.id}/jobs`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start job');
      openJob(data.job.id);
    } catch (err) {
      setError(err.message || 'Failed to start job');
    } finally {
      setStartingId(null);
    }
  };

  const handleDeleteCompletion = async (completion) => {
    if (!window.confirm('Remove this completion from the history?')) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/completions/${completion.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to remove completion');
      replaceChecklist(data.checklist);
    } catch (err) {
      setError(err.message || 'Failed to remove completion');
    }
  };

  // Returns true on success so callers can reset their form.
  const handleAddExtra = async (checklistId, date, note) => {
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/checklists/${checklistId}/extras`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, note })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to schedule extra occurrence');
      replaceChecklist(data.checklist);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to schedule extra occurrence');
      return false;
    }
  };

  const handleDeleteExtra = async (extra) => {
    if (!window.confirm('Remove this extra occurrence?')) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/extras/${extra.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to remove extra occurrence');
      replaceChecklist(data.checklist);
    } catch (err) {
      setError(err.message || 'Failed to remove extra occurrence');
    }
  };

  const editButton = canEditPermission && (
    <button onClick={toggleEditMode} style={editMode ? doneButtonStyle : editButtonStyle}>
      {editMode ? <><Check size={16} /> Done Editing</> : <><Pencil size={16} /> Edit</>}
    </button>
  );

  // --- One cleaning job ---
  if (selectedJobId) {
    return <CleaningJobDetail key={selectedJobId} jobId={selectedJobId} users={users} onBack={backToList} />;
  }

  // --- Detail view ---
  if (selectedId) {
    return (
      <div>
        <button onClick={backToList} style={backButtonStyle}>
          <ArrowLeft size={16} /> All checklists
        </button>

        {error && <p style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</p>}

        {loading && !selectedChecklist ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : !selectedChecklist ? (
          <div style={cardStyle}><p style={{ color: '#64748b', margin: 0 }}>Checklist not found, or you are not assigned to it.</p></div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h1 style={{ color: '#1e293b', marginBottom: '6px' }}>{selectedChecklist.name}</h1>
                <ChecklistMeta checklist={selectedChecklist} />
              </div>
              {editButton}
            </div>

            {editMode && (
              editingChecklistId === selectedChecklist.id ? (
                <ChecklistForm
                  initial={selectedChecklist}
                  users={users}
                  saving={savingChecklist}
                  onSave={(values) => handleUpdateChecklist(selectedChecklist.id, values)}
                  onCancel={() => setEditingChecklistId(null)}
                />
              ) : (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <button onClick={() => setEditingChecklistId(selectedChecklist.id)} style={secondaryButtonStyle}>
                    <CalendarClock size={16} /> Schedule &amp; assignees
                  </button>
                  <button onClick={() => handleDeleteChecklist(selectedChecklist)} style={dangerButtonStyle}>
                    <Trash2 size={16} /> Delete checklist
                  </button>
                </div>
              )
            )}

            <div style={{ ...cardStyle, marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ color: '#334155', fontSize: '14px' }}>
                  {selectedChecklist.recent_completions.length > 0 ? (
                    <>Last done {formatDateTime(selectedChecklist.recent_completions[0].completed_at)} by <strong>{selectedChecklist.recent_completions[0].completed_by_name}</strong></>
                  ) : (
                    'Not done yet.'
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedChecklist.next_job && (
                    <button onClick={() => openJob(selectedChecklist.next_job.id)} style={completeButtonStyle}>
                      <Play size={16} />
                      {selectedChecklist.next_job.status === 'in_progress'
                        ? ` Continue job (${selectedChecklist.next_job.item_done}/${selectedChecklist.next_job.item_total})`
                        : ` Open job for ${formatDate(selectedChecklist.next_job.scheduled_date)}`}
                    </button>
                  )}
                  {!(selectedChecklist.next_job?.actions.canStart || selectedChecklist.next_job?.actions.canWork) && (
                    <button
                      onClick={() => handleStartNow(selectedChecklist)}
                      disabled={startingId === selectedChecklist.id}
                      style={selectedChecklist.next_job ? { ...secondaryButtonStyle, padding: '10px 16px' } : completeButtonStyle}
                    >
                      <Play size={16} /> {startingId === selectedChecklist.id ? 'Starting...' : 'Start now'}
                    </button>
                  )}
                </div>
              </div>

              {selectedChecklist.recent_completions.length > 1 && (
                <div style={{ marginTop: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '6px' }}>Recent history</div>
                  {selectedChecklist.recent_completions.map((completion) => (
                    <div key={completion.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569', padding: '3px 0' }}>
                      <span style={{ flex: 1 }}>{formatDateTime(completion.completed_at)} · {completion.completed_by_name}</span>
                      {editMode && (
                        <button onClick={() => handleDeleteCompletion(completion)} style={{ ...iconDangerButtonStyle, padding: '2px 4px' }} title="Remove from history">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {editMode && selectedChecklist.recent_completions.length === 1 && (
                <button onClick={() => handleDeleteCompletion(selectedChecklist.recent_completions[0])} style={{ ...linkButtonStyle, marginTop: '8px' }}>
                  Undo last completion
                </button>
              )}
            </div>

            <ExtraOccurrences
              checklist={selectedChecklist}
              onAdd={(date, note) => handleAddExtra(selectedChecklist.id, date, note)}
              onDelete={handleDeleteExtra}
            />

            <ChecklistRooms checklist={selectedChecklist} propertyName={property.name} checklistId={selectedChecklist.id} editMode={editMode} canEditPermission={canEditPermission} onError={setError} />
          </>
        )}
      </div>
    );
  }

  // --- Overview ---
  return (
    <div>
      <button onClick={() => navigate(strBasePath(property))} style={backButtonStyle}>
        <ArrowLeft size={16} /> Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ color: '#1e293b', marginBottom: '6px' }}>Cleaning &amp; Maintenance</h1>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>Jobs to do, and the checklists they come from: regular cleaning, deep cleaning, lawn mowing and more.</p>
        </div>
        {editButton}
      </div>

      {error && <p style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</p>}

      <CleaningJobsPanel propertyKey={propertyKey} users={users} onOpenJob={openJob} refreshKey={checklists} />

      {editMode && (
        showNewForm ? (
          <ChecklistForm
            users={users}
            saving={savingChecklist}
            onSave={handleCreateChecklist}
            onCancel={() => setShowNewForm(false)}
          />
        ) : (
          <button onClick={() => setShowNewForm(true)} style={{ ...addButtonStyle, marginBottom: '20px' }}>
            <Plus size={16} /> New checklist
          </button>
        )
      )}

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading...</p>
      ) : checklists.length === 0 ? (
        <div style={cardStyle}>
          <p style={{ color: '#64748b', margin: 0 }}>
            No checklists{canEditPermission ? ' yet. Enter edit mode to add one.' : ' assigned to you.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
          {checklists.map((checklist, checklistIndex) => {
            const due = describeDue(checklist);
            const lastDone = checklist.recent_completions[0];
            return (
              <div key={checklist.id} style={{ ...cardStyle, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px' }} onClick={() => openChecklist(checklist.id)}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <h3 style={{ margin: 0, color: '#1e293b', fontSize: '17px', flex: 1, minWidth: 0 }}>{checklist.name}</h3>
                  {editMode && (
                    <MoveButtons
                      name={checklist.name}
                      index={checklistIndex}
                      count={checklists.length}
                      busy={movingChecklist}
                      onMove={(direction) => handleMoveChecklist(checklist, direction)}
                    />
                  )}
                  <ChevronRight size={18} color="#94a3b8" />
                </div>
                <span style={{ ...badgeStyle, color: due.color, backgroundColor: due.background }}>{due.label}</span>
                {checklist.next_job && (
                  <div style={metaLineStyle}>
                    <Play size={14} /> Job {formatDate(checklist.next_job.scheduled_date)}: {JOB_STATUS[checklist.next_job.status]?.label}
                    {checklist.next_job.status === 'in_progress' ? ` (${checklist.next_job.item_done}/${checklist.next_job.item_total})` : ''}
                  </div>
                )}
                <div style={metaLineStyle}><CalendarClock size={14} /> {describeSchedule(checklist)}</div>
                <div style={metaLineStyle}><Users size={14} /> {describeAssignees(checklist)}</div>
                <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                  {plural(checklist.task_count, 'task')} under {plural(checklist.room_count, 'title')}
                  {lastDone ? ` · last done ${new Date(lastDone.completed_at).toLocaleDateString()} by ${lastDone.completed_by_name}` : ' · never done'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && checklists.length > 0 && (
        <MaintenanceCalendar propertyKey={propertyKey} checklists={checklists} onOpenChecklist={openChecklist} onAddExtra={handleAddExtra} />
      )}
    </div>
  );
};

const ExtraOccurrences = ({ checklist, onAdd, onDelete }) => {
  const [date, setDate] = useState(() => toYmd(new Date()));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const extras = checklist.pending_extras || [];

  const submit = async () => {
    if (!date) return;
    setSaving(true);
    const ok = await onAdd(date, note.trim());
    setSaving(false);
    if (ok) setNote('');
  };

  return (
    <div style={{ ...cardStyle, marginBottom: '20px' }}>
      <h3 style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '15px' }}>Extra occurrences</h3>
      <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 10px' }}>
        Plan this checklist outside the regular schedule (e.g. after guests left a mess). Each one becomes a job. Finishing any job on this checklist covers extras up to today.
      </p>

      {extras.map((extra) => (
        <div key={extra.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', padding: '6px 0', borderTop: '1px solid #f1f5f9' }}>
          <CalendarPlus size={14} color="#c2410c" />
          <span style={{ fontWeight: 600 }}>{formatDate(extra.scheduled_date)}</span>
          <span style={{ flex: 1, color: '#64748b' }}>{extra.note || ''}{extra.created_by_name ? ` · added by ${extra.created_by_name}` : ''}</span>
          <button onClick={() => onDelete(extra)} style={{ ...iconDangerButtonStyle, padding: '2px 4px' }} title="Remove extra occurrence">
            <Trash2 size={12} />
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: extras.length > 0 ? '10px' : 0 }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
        <input
          type="text"
          placeholder="Reason (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          style={{ ...inputStyle, flex: 1, minWidth: '160px' }}
        />
        <button onClick={submit} disabled={saving || !date} style={extraButtonStyle}>
          <CalendarPlus size={16} /> {saving ? 'Saving...' : 'Schedule extra'}
        </button>
      </div>
    </div>
  );
};

const CHECKLIST_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#0d9488', '#9333ea'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MaintenanceCalendar = ({ propertyKey, checklists, onOpenChecklist, onAddExtra }) => {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [today, setToday] = useState(() => toYmd(new Date()));
  const [calendarError, setCalendarError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [extraChecklistId, setExtraChecklistId] = useState('');
  const [extraNote, setExtraNote] = useState('');
  const [savingExtra, setSavingExtra] = useState(false);

  // Grid runs Monday to Sunday and covers whole weeks around the month.
  const gridStart = new Date(month.getFullYear(), month.getMonth(), 1 - ((month.getDay() + 6) % 7));
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const gridEnd = new Date(monthEnd.getFullYear(), monthEnd.getMonth(), monthEnd.getDate() + (6 - ((monthEnd.getDay() + 6) % 7)));
  const from = toYmd(gridStart);
  const to = toYmd(gridEnd);

  useEffect(() => {
    let cancelled = false;
    setCalendarError('');
    authFetch(`${API_BASE}/api/cleaning/calendar${strPropertyQuery(propertyKey)}&from=${from}&to=${to}`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load calendar');
        if (cancelled) return;
        setEvents(data.events || []);
        setToday(data.today);
      })
      .catch((err) => { if (!cancelled) setCalendarError(err.message || 'Failed to load calendar'); });
    return () => { cancelled = true; };
  }, [from, to, checklists, propertyKey]);

  const colorFor = (checklistId) => {
    const index = checklists.findIndex((c) => c.id === checklistId);
    return CHECKLIST_COLORS[(index < 0 ? 0 : index) % CHECKLIST_COLORS.length];
  };

  const eventsByDay = {};
  events.forEach((event) => {
    if (!eventsByDay[event.date]) eventsByDay[event.date] = [];
    eventsByDay[event.date].push(event);
  });

  const days = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    days.push(d);
  }

  const shiftMonth = (delta) => {
    setMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    setSelectedDay(null);
  };

  const selectDay = (ymd) => {
    setSelectedDay((prev) => (prev === ymd ? null : ymd));
    setExtraChecklistId((prev) => prev || String(checklists[0]?.id || ''));
  };

  const submitExtra = async () => {
    if (!selectedDay || !extraChecklistId) return;
    setSavingExtra(true);
    const ok = await onAddExtra(Number(extraChecklistId), selectedDay, extraNote.trim());
    setSavingExtra(false);
    if (ok) setExtraNote('');
  };

  const describeEvent = (event) => {
    if (event.type === 'checkout') return 'Guest check-out';
    if (event.type === 'done') return `${event.name} done by ${event.by}`;
    if (event.type === 'extra') {
      return `Extra: ${event.name}${event.overdue ? ` (overdue since ${formatDate(event.originalDate)})` : ''}${event.note ? ` · ${event.note}` : ''}`;
    }
    return event.overdue ? `${event.name} (overdue since ${formatDate(event.originalDate)})` : event.name;
  };

  const selectedEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];

  return (
    <div style={{ ...cardStyle, marginTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '12px' }}>
        <button onClick={() => shiftMonth(-1)} style={iconButtonStyle} title="Previous month"><ChevronLeft size={16} /></button>
        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '16px' }}>
          {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </h3>
        <button onClick={() => shiftMonth(1)} style={iconButtonStyle} title="Next month"><ChevronRight size={16} /></button>
      </div>

      {calendarError && <p style={{ color: '#dc2626', fontSize: '13px' }}>{calendarError}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '4px' }}>
        {WEEKDAYS.map((w) => (
          <div key={w} style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px', fontWeight: 600, padding: '4px 0' }}>{w}</div>
        ))}
        {days.map((d) => {
          const ymd = toYmd(d);
          const dayEvents = eventsByDay[ymd] || [];
          const hasCheckout = dayEvents.some((e) => e.type === 'checkout');
          const items = dayEvents.filter((e) => e.type !== 'checkout');
          const inMonth = d.getMonth() === month.getMonth();
          return (
            <div
              key={ymd}
              onClick={() => selectDay(ymd)}
              style={{
                ...calendarDayStyle,
                backgroundColor: selectedDay === ymd ? '#eff6ff' : inMonth ? 'white' : '#f8fafc',
                borderColor: ymd === today ? '#2563eb' : selectedDay === ymd ? '#93c5fd' : '#e2e8f0'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2px' }}>
                <span style={{ fontSize: '12px', fontWeight: ymd === today ? 700 : 500, color: inMonth ? '#334155' : '#cbd5e1' }}>{d.getDate()}</span>
                {hasCheckout && <span title="Guest check-out"><LogOut size={11} color="#94a3b8" /></span>}
              </div>
              {items.slice(0, 3).map((event, i) => (
                <div key={i} title={describeEvent(event)} style={calendarChipStyle(event, colorFor(event.checklistId))}>
                  {event.type === 'done' ? '✓ ' : event.type === 'extra' ? '+ ' : ''}{event.name}
                </div>
              ))}
              {items.length > 3 && <div style={{ fontSize: '10px', color: '#64748b' }}>+{items.length - 3} more</div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '10px', fontSize: '11px', color: '#64748b' }}>
        <span><span style={legendSwatchStyle({ backgroundColor: '#2563eb' })} /> Planned</span>
        <span><span style={legendSwatchStyle({ backgroundColor: 'white', border: '1px dashed #c2410c' })} /> Extra</span>
        <span><span style={legendSwatchStyle({ backgroundColor: '#dc2626' })} /> Overdue</span>
        <span><span style={legendSwatchStyle({ backgroundColor: '#dcfce7', border: '1px solid #16a34a' })} /> Done</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><LogOut size={11} /> Guest check-out</span>
      </div>

      {selectedDay && (
        <div style={{ marginTop: '14px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
          <h4 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '14px' }}>{formatDate(selectedDay)}</h4>
          {selectedEvents.length === 0 && <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 8px' }}>Nothing planned.</p>}
          {selectedEvents.map((event, i) => (
            <div
              key={i}
              onClick={() => event.checklistId && onOpenChecklist(event.checklistId)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', padding: '4px 0', cursor: event.checklistId ? 'pointer' : 'default' }}
            >
              {event.type === 'checkout'
                ? <LogOut size={13} color="#94a3b8" />
                : <span style={{ width: '10px', height: '10px', borderRadius: '3px', flexShrink: 0, backgroundColor: event.overdue ? '#dc2626' : colorFor(event.checklistId) }} />}
              <span>{describeEvent(event)}</span>
            </div>
          ))}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
            <select value={extraChecklistId} onChange={(e) => setExtraChecklistId(e.target.value)} style={inputStyle}>
              {checklists.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input
              type="text"
              placeholder="Reason (optional)"
              value={extraNote}
              onChange={(e) => setExtraNote(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitExtra()}
              style={{ ...inputStyle, flex: 1, minWidth: '160px' }}
            />
            <button onClick={submitExtra} disabled={savingExtra || !extraChecklistId} style={extraButtonStyle}>
              <CalendarPlus size={16} /> {savingExtra ? 'Saving...' : 'Add extra on this day'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ChecklistMeta = ({ checklist }) => {
  const due = describeDue(checklist);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
      <span style={{ ...badgeStyle, color: due.color, backgroundColor: due.background }}>{due.label}</span>
      <span style={metaLineStyle}><CalendarClock size={14} /> {describeSchedule(checklist)}</span>
      <span style={metaLineStyle}><Users size={14} /> {describeAssignees(checklist)}</span>
    </div>
  );
};

const ChecklistForm = ({ initial, users, saving, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [useBookings, setUseBookings] = useState(initial ? Boolean(initial.booking_every) : true);
  const [bookingEvery, setBookingEvery] = useState(String(initial?.booking_every || 1));
  const [useInterval, setUseInterval] = useState(Boolean(initial?.interval_every));
  const [intervalEvery, setIntervalEvery] = useState(String(initial?.interval_every || 2));
  const [intervalUnit, setIntervalUnit] = useState(initial?.interval_unit || 'weeks');
  const [useSeason, setUseSeason] = useState(Boolean(initial?.season_start_month));
  const [seasonStart, setSeasonStart] = useState(String(initial?.season_start_month || 5));
  const [seasonEnd, setSeasonEnd] = useState(String(initial?.season_end_month || 11));
  const [assignees, setAssignees] = useState((initial?.assignees || []).map((a) => a.username));

  // Keep assignees that are no longer in the user list (e.g. deactivated) visible so they can be removed.
  const userOptions = [
    ...users,
    ...(initial?.assignees || []).filter((a) => !users.some((u) => u.username === a.username))
  ];

  const toggleAssignee = (username) => {
    setAssignees((prev) => (prev.includes(username) ? prev.filter((u) => u !== username) : [...prev, username]));
  };

  const hasRule = useBookings || useInterval;

  const submit = () => {
    if (!name.trim() || !hasRule) return;
    onSave({
      name: name.trim(),
      bookingEvery: useBookings ? Number(bookingEvery) : null,
      intervalEvery: useInterval ? Number(intervalEvery) : null,
      intervalUnit: useInterval ? intervalUnit : null,
      seasonStartMonth: useSeason ? Number(seasonStart) : null,
      seasonEndMonth: useSeason ? Number(seasonEnd) : null,
      assignees
    });
  };

  return (
    <div style={{ ...cardStyle, marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <label style={labelStyle}>Name</label>
        <input
          type="text"
          placeholder="e.g. Lawn mowing"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ ...inputStyle, width: '100%' }}
          autoFocus
        />
      </div>

      <div>
        <label style={labelStyle}>Schedule</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={ruleRowStyle}>
            <input type="checkbox" checked={useBookings} onChange={(e) => setUseBookings(e.target.checked)} style={{ margin: 0 }} />
            <span style={ruleTextStyle(useBookings)}>Every</span>
            <input
              type="number"
              min="1"
              step="1"
              value={bookingEvery}
              disabled={!useBookings}
              onChange={(e) => setBookingEvery(e.target.value)}
              style={{ ...inputStyle, width: '80px' }}
            />
            <span style={ruleTextStyle(useBookings)}>booking(s)</span>
          </div>
          <div style={ruleRowStyle}>
            <input type="checkbox" checked={useInterval} onChange={(e) => setUseInterval(e.target.checked)} style={{ margin: 0 }} />
            <span style={ruleTextStyle(useInterval)}>Every</span>
            <input
              type="number"
              min="1"
              step="1"
              value={intervalEvery}
              disabled={!useInterval}
              onChange={(e) => setIntervalEvery(e.target.value)}
              style={{ ...inputStyle, width: '80px' }}
            />
            <select value={intervalUnit} disabled={!useInterval} onChange={(e) => setIntervalUnit(e.target.value)} style={inputStyle}>
              <option value="weeks">week(s)</option>
              <option value="months">month(s)</option>
            </select>
          </div>
          <div style={ruleRowStyle}>
            <input type="checkbox" checked={useSeason} onChange={(e) => setUseSeason(e.target.checked)} style={{ margin: 0 }} />
            <span style={ruleTextStyle(useSeason)}>Only between</span>
            <select value={seasonStart} disabled={!useSeason} onChange={(e) => setSeasonStart(e.target.value)} style={inputStyle}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <span style={ruleTextStyle(useSeason)}>and</span>
            <select value={seasonEnd} disabled={!useSeason} onChange={(e) => setSeasonEnd(e.target.value)} style={inputStyle}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
        <p style={{ color: hasRule ? '#94a3b8' : '#dc2626', fontSize: '12px', margin: '6px 0 0' }}>
          {!hasRule
            ? 'Pick at least one rule.'
            : useBookings && useInterval
              ? 'Due on whichever comes first after it was last done: the check-out of the Nth confirmed booking, or the set time.'
              : useBookings
                ? 'Due on the check-out day of the Nth confirmed booking since it was last done.'
                : 'Due the set time after it was last done.'}
          {hasRule && useSeason && ' Outside the season it waits until the first day of the season.'}
        </p>
      </div>

      <div>
        <label style={labelStyle}>Who can do it</label>
        {userOptions.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>No users with cleaning access found.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {userOptions.map((user) => (
              <label key={user.username} style={assigneeChipStyle(assignees.includes(user.username))}>
                <input
                  type="checkbox"
                  checked={assignees.includes(user.username)}
                  onChange={() => toggleAssignee(user.username)}
                  style={{ margin: 0 }}
                />
                {user.displayName}
              </label>
            ))}
          </div>
        )}
        <p style={{ color: '#94a3b8', fontSize: '12px', margin: '6px 0 0' }}>
          {assignees.length === 0
            ? 'Nobody selected: everyone with cleaning access can see and do it.'
            : 'Only the selected users (and users who can edit checklists) can see and do it.'}
        </p>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={submit} disabled={saving || !name.trim() || !hasRule} style={addButtonStyle}>
          <Check size={16} /> {saving ? 'Saving...' : initial ? 'Save' : 'Create checklist'}
        </button>
        <button onClick={onCancel} style={{ ...smallCancelButtonStyle, padding: '8px 14px', fontSize: '14px' }}>Cancel</button>
      </div>
    </div>
  );
};

const ChecklistRooms = ({ checklist, propertyName, checklistId, editMode, canEditPermission, onError }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRooms, setExpandedRooms] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});

  const [newRoomName, setNewRoomName] = useState('');
  const [addingRoom, setAddingRoom] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState('');

  const [newTaskTitleByRoom, setNewTaskTitleByRoom] = useState({});
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskTitle, setEditingTaskTitle] = useState('');

  const [commentDraftByTask, setCommentDraftByTask] = useState({});
  const [commentPhotoByTask, setCommentPhotoByTask] = useState({});
  const [savingCommentTaskId, setSavingCommentTaskId] = useState(null);

  const taskPhotoInputRef = useRef(null);
  const [uploadingTaskPhotoId, setUploadingTaskPhotoId] = useState(null);
  const commentPhotoInputRef = useRef(null);
  const [pendingCommentPhotoTaskId, setPendingCommentPhotoTaskId] = useState(null);
  const [moving, setMoving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [nextStay, setNextStay] = useState(null);

  const setError = onError;

  const handleExportPdf = async () => {
    setExporting(true);
    setError('');
    try {
      await exportChecklistPdf({
        name: checklist.name,
        propertyName,
        details: [describeSchedule(checklist), `Who can do it: ${describeAssignees(checklist)}`],
        rooms: rooms.map((room) => ({
          ...room,
          tasks: (room.tasks || []).map((task) => ({ ...task, title: resolveTaskTitle(task.title, nextStay).text }))
        }))
      });
    } catch (err) {
      setError('Failed to create the PDF');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const loadRooms = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await authFetch(`${API_BASE}/api/cleaning/rooms?checklistId=${checklistId}`);
        if (!response.ok) throw new Error('Failed to load checklist');
        const data = await response.json();
        setRooms(data.rooms || []);
        setNextStay(data.nextStay || null);
      } catch (err) {
        setError(err.message || 'Failed to load checklist');
      } finally {
        setLoading(false);
      }
    };
    loadRooms();
  }, [checklistId, setError]);

  const toggleRoomExpanded = (roomId) => {
    setExpandedRooms((prev) => ({ ...prev, [roomId]: !prev[roomId] }));
  };

  const toggleTaskExpanded = (taskId) => {
    setExpandedTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  // --- Rooms ---
  const handleAddRoom = async () => {
    const name = newRoomName.trim();
    if (!name) return;
    setAddingRoom(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, checklistId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add title');
      setRooms((prev) => [...prev, data.room]);
      setNewRoomName('');
    } catch (err) {
      setError(err.message || 'Failed to add title');
    } finally {
      setAddingRoom(false);
    }
  };

  const startEditRoom = (room) => {
    setEditingRoomId(room.id);
    setEditingRoomName(room.name);
  };

  const cancelEditRoom = () => {
    setEditingRoomId(null);
    setEditingRoomName('');
  };

  const saveEditRoom = async (roomId) => {
    const name = editingRoomName.trim();
    if (!name) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/rooms/${roomId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update title');
      setRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, name: data.room.name } : r)));
      cancelEditRoom();
    } catch (err) {
      setError(err.message || 'Failed to update title');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this title and all its tasks? This cannot be undone.')) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/rooms/${roomId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete title');
      setRooms((prev) => prev.filter((r) => r.id !== roomId));
    } catch (err) {
      setError(err.message || 'Failed to delete title');
    }
  };

  const moveItem = async (route, id, direction, applyIds) => {
    setMoving(true);
    setError('');
    try {
      applyIds(await requestMove(route, id, direction));
    } catch (err) {
      setError(err.message || 'Failed to change the order');
    } finally {
      setMoving(false);
    }
  };

  const handleMoveRoom = (room, direction) => moveItem('rooms', room.id, direction, (ids) => {
    setRooms((prev) => applyOrder(prev, ids));
  });

  const handleMoveTask = (task, direction) => moveItem('tasks', task.id, direction, (ids) => {
    setRooms((prev) => prev.map((r) => (r.id === task.room_id ? { ...r, tasks: applyOrder(r.tasks, ids) } : r)));
  });

  // --- Tasks ---
  const handleAddTask = async (roomId) => {
    const title = (newTaskTitleByRoom[roomId] || '').trim();
    if (!title) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/rooms/${roomId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add task');
      setRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, tasks: [...(r.tasks || []), data.task] } : r)));
      setNewTaskTitleByRoom((prev) => ({ ...prev, [roomId]: '' }));
    } catch (err) {
      setError(err.message || 'Failed to add task');
    }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditingTaskTitle(task.title);
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditingTaskTitle('');
  };

  const saveEditTask = async (task) => {
    const title = editingTaskTitle.trim();
    if (!title) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update task');
      setRooms((prev) => prev.map((r) => (
        r.id === task.room_id
          ? { ...r, tasks: r.tasks.map((t) => (t.id === task.id ? { ...t, title: data.task.title } : t)) }
          : r
      )));
      cancelEditTask();
    } catch (err) {
      setError(err.message || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/tasks/${task.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete task');
      setRooms((prev) => prev.map((r) => (
        r.id === task.room_id ? { ...r, tasks: r.tasks.filter((t) => t.id !== task.id) } : r
      )));
    } catch (err) {
      setError(err.message || 'Failed to delete task');
    }
  };

  const handleToggleRequiresPhoto = async (task) => {
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/tasks/${task.id}/requires-photo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requiresPhoto: !task.requires_photo })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update task');
      setRooms((prev) => prev.map((r) => (
        r.id === task.room_id
          ? { ...r, tasks: r.tasks.map((t) => (t.id === task.id ? { ...t, requires_photo: data.task.requires_photo } : t)) }
          : r
      )));
    } catch (err) {
      setError(err.message || 'Failed to update task');
    }
  };

  const triggerTaskPhotoUpload = (taskId) => {
    setUploadingTaskPhotoId(taskId);
    if (taskPhotoInputRef.current) {
      taskPhotoInputRef.current.value = '';
      taskPhotoInputRef.current.click();
    }
  };

  const handleTaskPhotoSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    const taskId = uploadingTaskPhotoId;
    if (!file || !taskId) return;
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch(`${API_BASE}/api/cleaning/tasks/${taskId}/photo`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to upload photo');
      setRooms((prev) => prev.map((r) => (
        r.id === data.task.room_id
          ? { ...r, tasks: r.tasks.map((t) => (t.id === taskId ? { ...t, ...data.task } : t)) }
          : r
      )));
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploadingTaskPhotoId(null);
    }
  };

  const handleDeleteTaskPhoto = async (task) => {
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/tasks/${task.id}/photo`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete photo');
      setRooms((prev) => prev.map((r) => (
        r.id === task.room_id
          ? { ...r, tasks: r.tasks.map((t) => (t.id === task.id ? { ...t, ...data.task } : t)) }
          : r
      )));
    } catch (err) {
      setError(err.message || 'Failed to delete photo');
    }
  };

  // --- Comments ---
  const triggerCommentPhotoUpload = (taskId) => {
    setPendingCommentPhotoTaskId(taskId);
    if (commentPhotoInputRef.current) {
      commentPhotoInputRef.current.value = '';
      commentPhotoInputRef.current.click();
    }
  };

  const handleCommentPhotoSelected = (event) => {
    const file = event.target.files && event.target.files[0];
    const taskId = pendingCommentPhotoTaskId;
    if (!file || !taskId) return;
    setCommentPhotoByTask((prev) => ({ ...prev, [taskId]: file }));
  };

  const clearCommentPhoto = (taskId) => {
    setCommentPhotoByTask((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  const handleAddComment = async (task) => {
    const text = (commentDraftByTask[task.id] || '').trim();
    const photo = commentPhotoByTask[task.id];
    if (!text && !photo) return;

    setSavingCommentTaskId(task.id);
    setError('');
    try {
      const formData = new FormData();
      if (text) formData.append('text', text);
      if (photo) formData.append('file', photo);
      const response = await authFetch(`${API_BASE}/api/cleaning/tasks/${task.id}/comments`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add comment');
      setRooms((prev) => prev.map((r) => (
        r.id === task.room_id
          ? {
              ...r,
              tasks: r.tasks.map((t) => (
                t.id === task.id ? { ...t, comments: [...(t.comments || []), data.comment] } : t
              ))
            }
          : r
      )));
      setCommentDraftByTask((prev) => ({ ...prev, [task.id]: '' }));
      clearCommentPhoto(task.id);
    } catch (err) {
      setError(err.message || 'Failed to add comment');
    } finally {
      setSavingCommentTaskId(null);
    }
  };

  const handleDeleteComment = async (task, comment) => {
    if (!window.confirm('Delete this comment?')) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/cleaning/comments/${comment.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete comment');
      setRooms((prev) => prev.map((r) => (
        r.id === task.room_id
          ? {
              ...r,
              tasks: r.tasks.map((t) => (
                t.id === task.id ? { ...t, comments: (t.comments || []).filter((c) => c.id !== comment.id) } : t
              ))
            }
          : r
      )));
    } catch (err) {
      setError(err.message || 'Failed to delete comment');
    }
  };

  return (
    <div>
      <input
        type="file"
        accept="image/*,.heic,.heif"
        ref={taskPhotoInputRef}
        style={{ display: 'none' }}
        onChange={handleTaskPhotoSelected}
      />
      <input
        type="file"
        accept="image/*,.heic,.heif"
        ref={commentPhotoInputRef}
        style={{ display: 'none' }}
        onChange={handleCommentPhotoSelected}
      />

      {!loading && rooms.length > 0 && (
        <button onClick={handleExportPdf} disabled={exporting} style={{ ...secondaryButtonStyle, marginBottom: '16px' }}>
          <FileDown size={16} /> {exporting ? 'Creating PDF...' : 'Export PDF'}
        </button>
      )}

      {editMode && (
        <div style={{ ...cardStyle, marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <input
            type="text"
            placeholder="New title (e.g. Kitchen, Garden, Lawn)"
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddRoom()}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={handleAddRoom} disabled={addingRoom} style={addButtonStyle}>
            <Plus size={16} /> Add Title
          </button>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading...</p>
      ) : rooms.length === 0 ? (
        <div style={cardStyle}><p style={{ color: '#64748b', margin: 0 }}>No titles yet.{canEditPermission ? ' Enter edit mode to add one.' : ''}</p></div>
      ) : (
        rooms.map((room, roomIndex) => {
          const isExpanded = expandedRooms[room.id] !== false;
          const taskCount = (room.tasks || []).length;
          return (
            <div key={room.id} style={{ ...cardStyle, marginBottom: '14px', padding: 0, overflow: 'hidden' }}>
              <div
                style={roomHeaderStyle}
                onClick={() => toggleRoomExpanded(room.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 180px', minWidth: 0 }}>
                  {isExpanded ? <ChevronDown size={18} color="#64748b" /> : <ChevronRight size={18} color="#64748b" />}
                  {editingRoomId === room.id ? (
                    <input
                      type="text"
                      value={editingRoomName}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setEditingRoomName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveEditRoom(room.id)}
                      style={inputStyle}
                      autoFocus
                    />
                  ) : (
                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '16px' }}>{room.name}</h3>
                  )}
                  <span style={{ color: '#94a3b8', fontSize: '13px' }}>({taskCount} task{taskCount === 1 ? '' : 's'})</span>
                </div>

                {editMode && (
                  <div style={{ display: 'flex', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                    {editingRoomId === room.id ? (
                      <>
                        <button onClick={() => saveEditRoom(room.id)} style={smallSaveButtonStyle}>Save</button>
                        <button onClick={cancelEditRoom} style={smallCancelButtonStyle}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <MoveButtons
                          name={room.name}
                          index={roomIndex}
                          count={rooms.length}
                          busy={moving}
                          onMove={(direction) => handleMoveRoom(room, direction)}
                        />
                        <button onClick={() => startEditRoom(room)} style={iconButtonStyle} title="Rename title"><Pencil size={14} /></button>
                        <button onClick={() => handleDeleteRoom(room.id)} style={iconDangerButtonStyle} title="Delete title"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {isExpanded && (
                <div style={{ padding: '0 20px 16px' }}>
                  {(room.tasks || []).map((task, taskIndex) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      index={taskIndex}
                      count={taskCount}
                      nextStay={nextStay}
                      moving={moving}
                      onMove={(direction) => handleMoveTask(task, direction)}
                      editMode={editMode}
                      isExpanded={Boolean(expandedTasks[task.id])}
                      onToggleExpanded={() => toggleTaskExpanded(task.id)}
                      editingTaskId={editingTaskId}
                      editingTaskTitle={editingTaskTitle}
                      setEditingTaskTitle={setEditingTaskTitle}
                      onStartEdit={() => startEditTask(task)}
                      onCancelEdit={cancelEditTask}
                      onSaveEdit={() => saveEditTask(task)}
                      onDelete={() => handleDeleteTask(task)}
                      onToggleRequiresPhoto={() => handleToggleRequiresPhoto(task)}
                      onUploadPhoto={() => triggerTaskPhotoUpload(task.id)}
                      onDeletePhoto={() => handleDeleteTaskPhoto(task)}
                      uploadingPhoto={uploadingTaskPhotoId === task.id}
                      commentDraft={commentDraftByTask[task.id] || ''}
                      onCommentDraftChange={(value) => setCommentDraftByTask((prev) => ({ ...prev, [task.id]: value }))}
                      commentPhoto={commentPhotoByTask[task.id]}
                      onTriggerCommentPhoto={() => triggerCommentPhotoUpload(task.id)}
                      onClearCommentPhoto={() => clearCommentPhoto(task.id)}
                      onAddComment={() => handleAddComment(task)}
                      savingComment={savingCommentTaskId === task.id}
                      onDeleteComment={(comment) => handleDeleteComment(task, comment)}
                    />
                  ))}

                  {editMode && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <input
                        type="text"
                        placeholder="New task"
                        value={newTaskTitleByRoom[room.id] || ''}
                        onChange={(e) => setNewTaskTitleByRoom((prev) => ({ ...prev, [room.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTask(room.id)}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <button onClick={() => handleAddTask(room.id)} style={addButtonStyle}>
                        <Plus size={16} /> Add Task
                      </button>
                    </div>
                  )}
                  {editMode && (
                    <FormulaHelp title={newTaskTitleByRoom[room.id] || ''} nextStay={nextStay} />
                  )}

                  {!editMode && taskCount === 0 && (
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: '10px 0 0' }}>No tasks under this title.</p>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

// Under a task being written: what the formula shows right now, or how to write one.
const FormulaHelp = ({ title, nextStay }) => {
  const resolved = resolveTaskTitle(title, nextStay);
  return (
    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '6px 0 0' }}>
      {resolved.hasFormula ? (
        <>Shows as: <strong style={{ color: '#475569' }}>{resolved.text}</strong>{resolved.assumed ? ` · ${NO_NEXT_STAY_TEXT}` : ''}</>
      ) : (
        'Numbers from the next stay: write /days*3, /guests*2, /days*guests*3 or /guests*(1every3days) in the task.'
      )}
    </p>
  );
};

const TaskRow = ({
  task,
  index,
  count,
  nextStay,
  moving,
  onMove,
  editMode,
  isExpanded,
  onToggleExpanded,
  editingTaskId,
  editingTaskTitle,
  setEditingTaskTitle,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onToggleRequiresPhoto,
  onUploadPhoto,
  onDeletePhoto,
  uploadingPhoto,
  commentDraft,
  onCommentDraftChange,
  commentPhoto,
  onTriggerCommentPhoto,
  onClearCommentPhoto,
  onAddComment,
  savingComment,
  onDeleteComment
}) => {
  const isEditingThis = editingTaskId === task.id;
  const comments = task.comments || [];
  const resolvedTitle = resolveTaskTitle(task.title, nextStay);

  return (
    <div style={taskRowStyle}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        <button onClick={onToggleExpanded} style={taskExpandButtonStyle} title="Toggle comments">
          {isExpanded ? <ChevronDown size={16} color="#94a3b8" /> : <ChevronRight size={16} color="#94a3b8" />}
        </button>

        {isEditingThis ? (
          <input
            type="text"
            value={editingTaskTitle}
            onChange={(e) => setEditingTaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSaveEdit()}
            style={{ ...inputStyle, flex: 1 }}
            autoFocus
          />
        ) : (
          <span data-testid="task-title" style={{ flex: '1 1 160px', minWidth: 0, color: '#1e293b', fontSize: '14px' }}>{editMode ? task.title : resolvedTitle.text}</span>
        )}

        {!isEditingThis && !editMode && task.requires_photo && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#b45309', fontSize: '12px' }}>
            <Camera size={13} /> Photo required
          </span>
        )}

        {!isEditingThis && comments.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#94a3b8', fontSize: '12px' }}>
            <MessageCircle size={13} /> {comments.length}
          </span>
        )}

        {editMode && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
            {isEditingThis ? (
              <>
                <button onClick={onSaveEdit} style={smallSaveButtonStyle}>Save</button>
                <button onClick={onCancelEdit} style={smallCancelButtonStyle}>Cancel</button>
              </>
            ) : (
              <>
                <MoveButtons name={task.title} index={index} count={count} busy={moving} onMove={onMove} />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#475569', fontSize: '12px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  <input type="checkbox" checked={Boolean(task.requires_photo)} onChange={onToggleRequiresPhoto} />
                  Photo required
                </label>
                <button onClick={onUploadPhoto} style={iconButtonStyle} title="Add task photo" disabled={uploadingPhoto}>
                  <Camera size={14} />
                </button>
                <button onClick={onStartEdit} style={iconButtonStyle} title="Edit task"><Pencil size={14} /></button>
                <button onClick={onDelete} style={iconDangerButtonStyle} title="Delete task"><Trash2 size={14} /></button>
              </>
            )}
          </div>
        )}
      </div>

      {isEditingThis && <FormulaHelp title={editingTaskTitle} nextStay={nextStay} />}
      {!isEditingThis && editMode && resolvedTitle.hasFormula && <FormulaHelp title={task.title} nextStay={nextStay} />}
      {!editMode && resolvedTitle.assumed && (
        <p style={{ color: '#94a3b8', fontSize: '12px', margin: '4px 0 0 24px' }}>{NO_NEXT_STAY_TEXT}</p>
      )}

      {task.photo_file_name && (
        <div style={{ marginTop: '8px', position: 'relative', display: 'inline-block' }}>
          <AuthedImage
            fileName={task.photo_file_name}
            alt={task.photo_original_name || 'Task'}
            style={{ maxWidth: '160px', maxHeight: '160px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
          {editMode && (
            <button onClick={onDeletePhoto} style={removePhotoButtonStyle} title="Remove photo">
              <X size={12} />
            </button>
          )}
        </div>
      )}

      {isExpanded && (
        <div style={{ marginTop: '10px', paddingLeft: '24px', borderLeft: '2px solid #f1f5f9' }}>
          {comments.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 8px' }}>No comments yet.</p>
          )}
          {comments.map((c) => (
            <div key={c.id} style={commentStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <div>
                  <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>{c.author_name || 'Unknown'}</span>
                  <span style={{ color: '#94a3b8', fontSize: '12px', marginLeft: '8px' }}>{formatDateTime(c.created_at)}</span>
                </div>
                <button onClick={() => onDeleteComment(c)} style={{ ...iconDangerButtonStyle, padding: '2px 4px' }} title="Delete comment">
                  <Trash2 size={12} />
                </button>
              </div>
              {c.text && <p style={{ margin: '4px 0 0', color: '#334155', fontSize: '13px', whiteSpace: 'pre-wrap' }}>{c.text}</p>}
              {c.photo_file_name && (
                <AuthedImage
                  fileName={c.photo_file_name}
                  alt={c.photo_original_name || 'Comment'}
                  style={{ marginTop: '6px', maxWidth: '140px', maxHeight: '140px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              )}
            </div>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                placeholder="Add a comment..."
                value={commentDraft}
                onChange={(e) => onCommentDraftChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAddComment()}
                style={{ ...inputStyle, flex: 1, fontSize: '13px' }}
              />
              <button onClick={onTriggerCommentPhoto} style={iconButtonStyle} title="Attach photo">
                <Camera size={14} />
              </button>
              <button onClick={onAddComment} disabled={savingComment} style={smallSaveButtonStyle}>
                {savingComment ? '...' : 'Post'}
              </button>
            </div>
            {commentPhoto && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                <Camera size={12} /> {commentPhoto.name}
                <button onClick={onClearCommentPhoto} style={{ ...iconDangerButtonStyle, padding: '2px 4px' }}><X size={10} /></button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const backButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' };
const roomHeaderStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '16px 20px', cursor: 'pointer' };
const taskRowStyle = { padding: '10px 0', borderTop: '1px solid #f1f5f9' };
const taskExpandButtonStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' };
const commentStyle = { backgroundColor: '#f8fafc', borderRadius: '8px', padding: '8px 10px', marginBottom: '6px' };
const inputStyle = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', color: '#334155', fontSize: '13px', fontWeight: 600, marginBottom: '6px' };
const badgeStyle = { display: 'inline-block', alignSelf: 'flex-start', borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 600 };
const metaLineStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#475569', fontSize: '13px' };
const editButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '20px' };
const doneButtonStyle = { ...editButtonStyle, backgroundColor: '#059669' };
const addButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' };
const completeButtonStyle = { ...addButtonStyle, backgroundColor: '#059669', padding: '10px 16px' };
const secondaryButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const dangerButtonStyle = { ...secondaryButtonStyle, backgroundColor: '#fef2f2', color: '#dc2626' };
const linkButtonStyle = { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px', padding: 0 };
const smallSaveButtonStyle = { backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
const smallCancelButtonStyle = { backgroundColor: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' };
const iconButtonStyle = { backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' };
const moveButtonStyle = (disabled) => ({ ...iconButtonStyle, opacity: disabled ? 0.35 : 1, cursor: disabled ? 'default' : 'pointer' });
const iconDangerButtonStyle = { backgroundColor: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' };
const removePhotoButtonStyle = { position: 'absolute', top: '-6px', right: '-6px', backgroundColor: '#dc2626', color: 'white', border: '2px solid white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 };
const extraButtonStyle = { ...addButtonStyle, backgroundColor: '#c2410c' };
const ruleRowStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' };
const ruleTextStyle = (active) => ({ color: active ? '#475569' : '#cbd5e1', fontSize: '14px' });
const calendarDayStyle = { minHeight: '78px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden', minWidth: 0 };
const calendarChipStyle = (event, color) => {
  const base = { fontSize: '10px', lineHeight: '14px', borderRadius: '4px', padding: '1px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  if (event.type === 'done') return { ...base, backgroundColor: '#dcfce7', color: '#166534' };
  if (event.overdue) return { ...base, backgroundColor: '#dc2626', color: 'white' };
  if (event.type === 'extra') return { ...base, backgroundColor: 'white', color: '#c2410c', border: '1px dashed #c2410c' };
  return { ...base, backgroundColor: color, color: 'white' };
};
const legendSwatchStyle = (extra) => ({ display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px', verticalAlign: 'middle', marginRight: '4px', boxSizing: 'border-box', ...extra });
const assigneeChipStyle = (selected) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '999px', cursor: 'pointer', fontSize: '13px',
  border: `1px solid ${selected ? '#2563eb' : '#e2e8f0'}`, backgroundColor: selected ? '#eff6ff' : 'white', color: selected ? '#1d4ed8' : '#334155'
});

export default CleaningPage;
